/**
 * Feedback submission + admin reading.
 *
 * Submission is a bare `fetch` with no credentials: the endpoint is public
 * because the answers we most need come from teachers at a demo who have no
 * account. Admin reads go through the authenticated axios instance.
 */

// The shared axios instance already carries the /api prefix in its baseURL, so
// paths here start at /admin — writing '/api/admin/...' produces '/api/api/...'
// and a 404 that only shows up at runtime. Raw fetch() calls DO need the prefix.
import api from './api';

const API_BASE_URL = import.meta.env.VITE_API_URL || '';

export interface SubmitFeedbackInput {
  subject: string;
  role: string;
  formKey: string;
  shareToken?: string | null;
  courseId?: string | null;
  chapter?: string | null;
  answers: Record<string, unknown>;
  comment?: string | null;
  cannotExplain?: string | null;
  wishlist?: string | null;
  priority?: number | null;
  learnerAccountId?: string | null;
  name?: string | null;
  email?: string | null;
  organisation?: string | null;
  respondentKey?: string | null;
  source?: string | null;
  /** Honeypot. Real forms leave this empty; bots fill it. */
  website?: string;
}

export async function submitFeedback(input: SubmitFeedbackInput): Promise<{ id: string }> {
  const res = await fetch(`${API_BASE_URL}/api/feedback`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  });
  if (!res.ok) throw new Error(`Feedback submit failed: ${res.status}`);
  return res.json();
}

export interface FeedbackRow {
  id: string;
  subject: string;
  role: string;
  formKey: string;
  status: string;
  shareToken: string | null;
  courseId: string | null;
  chapter: string | null;
  /** The lesson the response is about; null for feedback not tied to one. */
  lesson: { title: string | null; className: string | null; chapter: string | null } | null;
  answers: Record<string, unknown>;
  comment: string | null;
  cannotExplain: string | null;
  wishlist: string | null;
  priority: number | null;
  name: string | null;
  email: string | null;
  organisation: string | null;
  source: string | null;
  adminNote: string | null;
  submittedAt: string;
}

export interface FeedbackFilters {
  subject?: string;
  role?: string;
  status?: string;
  formKey?: string;
  chapter?: string;
  source?: string;
  withText?: boolean;
  limit?: number;
}

function toParams(f: FeedbackFilters): Record<string, string> {
  const p: Record<string, string> = {};
  if (f.subject) p.subject = f.subject;
  if (f.role) p.role = f.role;
  if (f.status) p.status = f.status;
  if (f.formKey) p.formKey = f.formKey;
  if (f.chapter) p.chapter = f.chapter;
  if (f.source) p.source = f.source;
  if (f.withText) p.withText = '1';
  if (f.limit) p.limit = String(f.limit);
  return p;
}

export async function adminListFeedback(f: FeedbackFilters = {}): Promise<{ items: FeedbackRow[]; total: number }> {
  const res = await api.get('/admin/feedback', { params: toParams(f) });
  return res.data;
}

export interface FeedbackSummary {
  total: number;
  byRole: Record<string, number>;
  byForm: Record<string, { count: number; questions: Record<string, Record<string, number>> }>;
}

export async function adminFeedbackSummary(f: FeedbackFilters = {}): Promise<FeedbackSummary> {
  const res = await api.get('/admin/feedback/summary', { params: toParams(f) });
  return res.data;
}

export async function adminUpdateFeedback(id: string, status: string, adminNote?: string): Promise<void> {
  await api.patch(`/admin/feedback/${id}`, { status, adminNote });
}

/** Builds the authenticated CSV URL — the browser follows it directly. */
export function feedbackExportUrl(f: FeedbackFilters = {}): string {
  const qs = new URLSearchParams(toParams(f)).toString();
  return `${API_BASE_URL}/api/admin/feedback/export${qs ? `?${qs}` : ''}`;
}
